import { useCallback, useEffect, useRef } from 'react';

import { Logger } from '@/utils/logging';

import { READY_TIMEOUT_MS } from './bnplInstallmentConstants';

export type UseMessagingReadyWatchdogOptions = {
  enabled: boolean;
  expectedSignals: number;
  onReady: () => void;
  logContext: Record<string, unknown>;
};

/**
 * A blank row is indistinguishable from "BNPL isn't offered here", and Stripe
 * reports no error when it silently never renders — so complain after
 * READY_TIMEOUT_MS unless every expected instance reported ready or the
 * caller dismissed the watchdog (no_content / failed) — a later ready still
 * reaches onReady.
 */
export const useMessagingReadyWatchdog = ({
  enabled,
  expectedSignals,
  onReady,
  logContext,
}: UseMessagingReadyWatchdogOptions) => {
  const readyRef = useRef<Record<string, true>>({});
  const firedRef = useRef(false); // onReady has already fired
  const answeredRef = useRef(false); // Stripe answered; stop the warning

  const reportReady = useCallback(
    (key: string) => {
      if (firedRef.current || readyRef.current[key]) return;
      readyRef.current[key] = true;
      if (Object.keys(readyRef.current).length >= expectedSignals) {
        firedRef.current = true;
        answeredRef.current = true;
        onReady();
      }
    },
    [expectedSignals, onReady],
  );

  const dismiss = useCallback(() => {
    answeredRef.current = true;
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const timeout = setTimeout(() => {
      if (answeredRef.current) return;
      Logger.warn('BNPL installment message never became ready', {
        ...logContext,
        ready: readyRef.current,
      });
    }, READY_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [enabled, logContext]);

  return { reportReady, dismiss };
};
